import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { handleError, AuthError } from "@/lib/errorHandling";
import { AuthState } from "@/types/auth";

class AuthManager {
  private static instance: AuthManager;
  private authState: AuthState = {
    user: null,
    session: null,
    profile: null,
    loading: true,
    initialized: false,
    error: null
  };
  private subscribers: Set<(state: AuthState) => void> = new Set();
  private initialized = false;
  private subscription: any = null;

  private constructor() {}

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  subscribe(callback: (state: AuthState) => void): () => void {
    this.subscribers.add(callback);
    
    // Immediately call with current state
    callback(this.authState);
    
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.authState));
  }

  private setAuthState(newState: Partial<AuthState>) {
    this.authState = { ...this.authState, ...newState };
    this.notifySubscribers();
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    console.log("AuthManager: Initializing auth...");
    this.initialized = true;

    try {
      // Check if sign-out is in progress - if so, skip session restoration
      // BUT: If we have a stored auth state, the sign-out flag might be stale
      const signOutInProgress = localStorage.getItem('authSignOutInProgress');
      const globalSignOutInProgress = (window as any).__authSignOutInProgress;
      const hasStoredAuthState = !!localStorage.getItem('deepFocus_authState');
      
      // If sign-out flag is set but we have stored auth state, it's likely stale
      // Clear it and proceed with session restoration
      if ((signOutInProgress === 'true' || globalSignOutInProgress) && hasStoredAuthState) {
        console.log('🔧 AuthManager: Sign-out flag found but stored auth state exists - clearing stale flag');
        localStorage.removeItem('authSignOutInProgress');
        if ((window as any).__authSignOutInProgress) {
          (window as any).__authSignOutInProgress = false;
        }
      } else if (signOutInProgress === 'true' || globalSignOutInProgress) {
        // If sign-out flag is set but no stored auth state, it's likely stale from a previous session
        // Clear it to allow normal auth flow
        console.log('🔧 AuthManager: Sign-out flag found but no stored auth state - clearing stale flag');
        localStorage.removeItem('authSignOutInProgress');
        if ((window as any).__authSignOutInProgress) {
          (window as any).__authSignOutInProgress = false;
        }
        // Continue with normal session restoration
      }
      
      // Get initial session
      console.log("AuthManager: Getting initial session...");
      const { data, error } = await supabase.auth.getSession();
      let { session } = data;
      
      if (error) {
        throw new AuthError("Failed to get session", "SESSION_ERROR");
      }

      // If no session from Supabase, try to restore from stored auth state
      if (!session) {
        console.log("AuthManager: No session from Supabase, checking stored auth state...");
        const storedAuthStateStr = localStorage.getItem('deepFocus_authState');
        if (storedAuthStateStr) {
          try {
            const storedAuthState = JSON.parse(storedAuthStateStr);
            if (storedAuthState.session && storedAuthState.user) {
              // Check if session is still valid (not expired)
              const expiresAt = storedAuthState.session.expires_at;
              if (expiresAt && expiresAt * 1000 > Date.now()) {
                console.log("AuthManager: Restoring session from stored auth state");
                session = storedAuthState.session;
                // Set the session in Supabase storage
                await supabase.auth.setSession({
                  access_token: session.access_token,
                  refresh_token: session.refresh_token
                });
              } else {
                console.log("AuthManager: Stored session expired, clearing auth state");
                localStorage.removeItem('deepFocus_authState');
              }
            }
          } catch (parseError) {
            console.error("AuthManager: Error parsing stored auth state:", parseError);
            localStorage.removeItem('deepFocus_authState');
          }
        }
      }

      console.log("AuthManager: Initial session retrieved:", { hasSession: !!session });
      await this.handleAuthChange(session);

      // Set up auth state listener
      console.log("AuthManager: Setting up auth state listener...");
      this.subscription = supabase.auth.onAuthStateChange((event, session) => {
        console.log("AuthManager: Auth state change event:", event, { hasSession: !!session });
        this.handleAuthChange(session);
      });

    } catch (error) {
      console.error("AuthManager: Auth initialization failed:", error);
      handleError(error, "initializing auth");
      this.setAuthState({
        loading: false,
        initialized: true,
        error: error instanceof Error ? error.message : "Failed to initialize authentication"
      });
    }
  }

  private async handleAuthChange(session: Session | null): Promise<void> {
    console.log("AuthManager: Auth change event:", { session: !!session, userId: session?.user?.id });
    
    try {
      if (!session) {
        console.log("AuthManager: No session, clearing auth state");
        this.setAuthState({
          user: null,
          session: null,
          profile: null,
          loading: false,
          initialized: true,
          error: null
        });
        return;
      }

      console.log("AuthManager: Session found, fetching profile before setting initialized");
      // Set session and user but keep loading true until profile is fetched
      this.setAuthState({
        user: session.user,
        session,
        loading: true,
        initialized: false,
        error: null
      });

      // Fetch profile and only set initialized after profile is loaded
      try {
        const profile = await this.ensureProfileExists(session.user.id, session.user.user_metadata);
        console.log("AuthManager: Profile loaded, setting initialized to true");
        this.setAuthState({
          profile,
          loading: false,
          initialized: true
        });
      } catch (error) {
        console.error("AuthManager: Profile fetch failed:", error);
        // Set initialized even if profile fails - user can still use the app
        this.setAuthState({
          profile: null,
          loading: false,
          initialized: true
        });
      }

    } catch (error) {
      console.error("AuthManager: Error in handleAuthChange:", error);
      handleError(error, "handling auth state change");
      this.setAuthState({
        loading: false,
        initialized: true,
        error: error instanceof Error ? error.message : "Auth initialization failed"
      });
    }
  }

  private async ensureProfileExists(userId: string, userMetadata?: any): Promise<any> {
    try {
      console.log("AuthManager: Ensuring profile exists for user:", userId);
      
      // First try to fetch existing profile
      const { data: existingProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("profile_type, display_name")
        .eq("user_id", userId)
        .maybeSingle();

      if (fetchError) {
        console.error("AuthManager: Profile fetch error:", fetchError);
        throw new AuthError("Failed to fetch profile", "PROFILE_FETCH_ERROR");
      }

      if (existingProfile) {
        console.log("AuthManager: Profile already exists:", existingProfile);
        return existingProfile;
      }

      console.log("AuthManager: Profile not found, creating new profile...");
      
      const displayName = userMetadata?.full_name || 
                         userMetadata?.name || 
                         userMetadata?.display_name || 
                         userMetadata?.email?.split('@')[0] || 
                         'User';
      
      const avatarUrl = userMetadata?.avatar_url || 
                       userMetadata?.picture || 
                       null;

      const { data: newProfile, error } = await supabase
        .from("profiles")
        .insert({
          user_id: userId,
          display_name: displayName,
          avatar_url: avatarUrl
        })
        .select("profile_type, display_name")
        .single();

      if (error) {
        console.error("AuthManager: Profile creation error:", error);
        throw new AuthError("Failed to create user profile", "PROFILE_CREATE_ERROR");
      }
      
      console.log("AuthManager: Profile created successfully:", newProfile);
      return newProfile;
    } catch (error) {
      console.error("AuthManager: Error in ensureProfileExists:", error);
      handleError(error, "ensuring profile exists");
      return null;
    }
  }

  getCurrentState(): AuthState {
    return this.authState;
  }

  retry(): void {
    console.log("AuthManager: Retrying auth initialization");
    this.setAuthState({ loading: true, error: null, initialized: false });
  }

  destroy(): void {
    console.log("AuthManager: Destroying auth manager");
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscribers.clear();
    this.initialized = false;
  }
}

export const authManager = AuthManager.getInstance();
